import { useMemo, useRef, useState } from 'react';
import { isNil, negate, values, omit, assignIn } from 'lodash';
import { Box } from '@material-ui/core';

import Summary from './summary/Summary';
import SiteMapPanel from '../../map/SiteMapPanel';
import { useDataSource } from '../../../services/ApiDataService';
import { getWorstStatus } from '../../../utilities/StatusUtilities';
import { useGeoJsonMapOverlay } from '../../../services/SystemConfigService';
import { statusFilterId } from '../../filters';
import { useSetIsLoading } from './MapPanelLoadingContext';

// calculated based on summary size and padding
const SUMMARY_HEIGHT = 33;

const summaryDataSource = {
    url: '/query/table/summary',
    params: {
        type: 'site',
        metrics: ['statusCounts'],
    },
};

const sitesDataSource = {
    url: '/query/table/direct',
    params: {
        type: 'site',
        metrics: ['status'],
        entityFields: 'name,description,notes,tags',
        renderAsEntity: true,
        rowStart: 0,
        rowLimit: 20000,
        sort: '-status',
    },
};

const matrixDataSource = {
    url: '/query/matrix',
    params: {
        type: 'businessService,site',
        entityFields: 'name,tags',
        metrics: 'status,bsSiteCounts,bsTestExperience',
        invertEntityFilters: true,
        noParent: true,
        pivot: 'bsSiteCounts',
        renderAsEntity: true,
        rowStart: 0,
        rowLimit: 20000,
        columnLimit: 20000,
    },
};

function extractMapFromMatrix(matrix, statusFilter = null) {
    if (!matrix) return { data: [] };

    let data = matrix.columns.map((colData) => {
        let statuses = matrix.data.map((rows) => {
            const row = rows[colData.order];
            return row?.bsSiteCounts?.worst;
        });

        if (statusFilter !== null) {
            const filter = statusFilter.status.split(',');
            statuses = statuses.filter((status) => {
                return filter.includes(status);
            });
        }

        if (statusFilter !== null && statuses.length === 0) {
            return null;
        }

        return {
            // Make sure we are preserving important metadata like plmId
            // At the top level of the structure, which is used by pgm
            ...colData,

            status: { worst: getWorstStatus(statuses) },
            site: colData,
        };
    });

    data = data.filter(negate(isNil));

    const statusCounts = data.reduce(
        (counts, { status: { worst } }) => {
            // eslint-disable-next-line no-plusplus
            counts[`${worst}Count`]++;
            return counts;
        },
        {
            redCount: 0,
            orangeCount: 0,
            yellowCount: 0,
            greenCount: 0,
            grayCount: 0,
        }
    );

    return {
        summary: { data: [{ statusCounts }] },
        sites: { data },
    };
}

export default function SummaryAndMap({ id, storageKey, filterState }) {
    const hasBSFilter = !!filterState?.parameters?.BusinessServicesGroup;

    const [search, setSearch] = useState();
    const geojson = useGeoJsonMapOverlay()?.geoJsonFile;
    const placeholder = <Box height={SUMMARY_HEIGHT} />;

    return (
        <SummaryAndMapDataSource filterState={filterState} search={search} hasBSFilter={hasBSFilter}>
            {({ summary, sites }) => (
                <>
                    {filterState ? <Summary summary={summary} height={SUMMARY_HEIGHT} /> : placeholder}

                    <SiteMapPanel
                        id={id}
                        geojson={geojson}
                        storageKey={storageKey}
                        onSearchChange={setSearch}
                        filterState={filterState}
                        sites={sites}
                    />
                </>
            )}
        </SummaryAndMapDataSource>
    );
}

function SummaryAndMapDataSource(props) {
    const { children, hasBSFilter, filterState, search } = props;

    const DataSource = hasBSFilter ? MatrixDataSource : SitesDataSource;

    return (
        <DataSource filterState={filterState} search={search}>
            {children}
        </DataSource>
    );
}

function SitesDataSource(props) {
    const { children, filterState, search } = props;
    const params = useFilterStateParams(filterState);
    const paramsWithSearch = useMemo(() => ({ ...params, ...(search ? { search } : {}) }), [params, search]);

    const [summary, isLoadingSummary] = useDataSource(summaryDataSource, params);
    const [sites, isLoadingSites] = useDataSource(sitesDataSource, paramsWithSearch);

    useSetIsLoading(isLoadingSummary);
    useSetIsLoading(isLoadingSites);

    return children({ summary, sites });
}

function MatrixDataSource(props) {
    const { children, filterState, search } = props;

    const baseParams = useFilterStateParams(filterState, [statusFilterId]);

    const params = useMemo(
        () => ({
            ...baseParams,
            ...(search ? { search } : {}),
        }),
        [baseParams, search]
    );

    const [matrix, isLoadingMatrix] = useDataSource(matrixDataSource, params);

    useSetIsLoading(isLoadingMatrix);

    const { summary, sites } = useMemo(
        () => extractMapFromMatrix(matrix, filterState?.parameters?.Status),
        [filterState, matrix]
    );

    return children({
        summary,
        sites,
    });
}

function useFilterStateParams(filterState, filtersToOmit) {
    const omitRef = useRef();
    omitRef.current = filtersToOmit;

    return useMemo(() => assignIn({}, ...values(omit(filterState?.parameters, omitRef.current))), [filterState]);
}
